import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteFreeSoloValueMapping, AutocompleteProps, AutocompleteRenderOptionState, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";
import Api from "../../../../../core/services/api/api";


interface TerritoryOption {
    id: string;
    code?: string;
    name?: string;
    description?: string;
}

interface TerritoryAutocompleteProps<T = TerritoryOption, M extends boolean = false, DC extends boolean = false, FS extends boolean = false> extends Omit<AutocompleteProps<T, M, DC, FS>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    transformRecord?: (record: TerritoryOption) => T;
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: T,
        state: AutocompleteRenderOptionState,
        defaultRender: (props: React.HTMLAttributes<HTMLLIElement>, option: T) => React.ReactNode
    ) => React.ReactNode;

    [key: string]: any;
}

const TerritoryAutocomplete = <T = TerritoryOption, M extends boolean = false, DC extends boolean = false, FS extends boolean = false>({
                                                                                                                                           textFieldProps = {},
                                                                                                                                           form = false,
                                                                                                                                           name = undefined,
                                                                                                                                           required = false,
                                                                                                                                           setLoadingOptions: setLoadingOptionsProps,
                                                                                                                                           renderOption: customRenderOption,
                                                                                                                                           transformRecord,
                                                                                                                                           ...props
                                                                                                                                       }: TerritoryAutocompleteProps<T, M, DC, FS>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches Territory options based on search keywords
     * Handles loading state and component unmounting
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string
     * @returns Array of matching Territory options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken, keywords?: string) => {
        setLoadingOptions(true);
        try {
            // @ts-ignore - API type definition is not complete
            const response = await Api.listTerritories({
                paginationInformation: {pageSize: 20, currentPage: 1},
                query: keywords,
            }, cancelToken)
            if (!isMounted())
                return [];
            return response?.data?.items?.map(e => transformRecord?.(e) ?? e as TerritoryOption) ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted, transformRecord]);

    /**
     * Determines if two Territory options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: TerritoryOption, value: TerritoryOption | null) =>
        option.id === value?.id || (option.code === value?.code && option.code != null), []);

    /**
     * Extracts the display label from a Territory option
     */
    const getOptionLabel = useCallback((option: TerritoryOption | AutocompleteFreeSoloValueMapping<FS>) => props.freeSolo === true ? option : `${(option as TerritoryOption)?.code ?? ""} ${((option as TerritoryOption)?.name ? `(${(option as TerritoryOption)?.name})` : '')}`, [props.freeSolo]);

    /**
     * Gets the unique key for a Territory option
     */
    const getOptionKey = useCallback((option: TerritoryOption | AutocompleteFreeSoloValueMapping<FS>) => props.freeSolo === true ? option : (option as TerritoryOption)?.id ?? (((option as TerritoryOption).code != null) ? `code_${(option as TerritoryOption).code}` : null) ?? "", [props.freeSolo]);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<T, M, DC, FS>
            keywordsBased
            canSearchEmptyKeywords
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue as (option: T, value: T | null) => boolean}
            getOptionLabel={getOptionLabel as (option: T | AutocompleteFreeSoloValueMapping<FS>) => string}
            getOptionKey={getOptionKey as (option: T | AutocompleteFreeSoloValueMapping<FS>) => string}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default TerritoryAutocomplete;